export const GPS_DEVICE_TYPE_ID = 8;
export const LIVE_WINDOW_MS = 3 * 60 * 1000;
export const ON_SITE_METERS = 150;
export const METERS_PER_MILE = 1609.344;

// 0 is a valid reading, so only null/undefined/""/NaN are treated as missing.
export const toNumber = (value) => {
  if (value == null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

// Guard null/"" explicitly: Number(null) is 0. Trackers also report 0,0 before their first fix.
export const toPosition = (lat, lng) => {
  const next = { lat: toNumber(lat), lng: toNumber(lng) };
  if (next.lat == null || next.lng == null) return null;
  if (next.lat === 0 && next.lng === 0) return null;
  return next;
};

// GPS payloads mix epoch seconds (`timestamp`), epoch ms, and ISO strings (`tx_reported`, `last_updated`).
export const toTimestampMs = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "number") return value < 1e12 ? value * 1000 : value;
  const text = String(value);
  if (/^\d{10}$/.test(text)) return Number(text) * 1000;
  if (/^\d{13}$/.test(text)) return Number(text);
  const parsed = Date.parse(text);
  return Number.isNaN(parsed) ? null : parsed;
};

export const isLive = (lastUpdatedMs, now = Date.now()) =>
  lastUpdatedMs != null && now - lastUpdatedMs <= LIVE_WINDOW_MS;

export function distanceMeters(from, to) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const earthRadius = 6371000;
  const dLat = toRad(to.lat - from.lat);
  const dLng = toRad(to.lng - from.lng);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(from.lat)) * Math.cos(toRad(to.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * earthRadius * Math.asin(Math.sqrt(a));
}

export const compassPoint = (degrees) => {
  const points = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  const normalized = (((toNumber(degrees) ?? 0) % 360) + 360) % 360;
  return points[Math.round(normalized / 45) % 8];
};

export function siteStatus(position, site) {
  if (!position || !site?.position) return null;
  const meters = distanceMeters(position, site.position);
  if (meters <= ON_SITE_METERS) return { label: "On site", onSite: true, meters };
  return { label: `${(meters / METERS_PER_MILE).toFixed(1)} mi from site`, onSite: false, meters };
}

/**
 * Normalizes one entry of the gateway's linked devices (REST `latestWebhookData.children/parents`
 * or socket `gatewayWebhook.linked_devices`). Returns null for anything that cannot be plotted.
 */
export function normalizeLinkedVehicle(link) {
  const deviceTypeId = Number(link?.device_data?.device_type_id ?? link?.device_type_id);
  if (deviceTypeId !== GPS_DEVICE_TYPE_ID) return null;

  const value = link?.device_value ?? {};
  const position = toPosition(value.position_latitude, value.position_longitude);
  if (!position) return null;

  return {
    id: String(link.device_id),
    name: link?.device_data?.name || `Device ${link.device_id}`,
    position,
    heading: toNumber(value.position_direction) ?? 0,
    speed: toNumber(value.convert_position_speed),
    motion: String(value.moment_status || "unknown").trim().toLowerCase(),
    motionLabel: value.moment_status || "Unknown",
    lastUpdatedMs: toTimestampMs(value.tx_reported ?? value.last_updated ?? value.timestamp),
    locationLabel:
      link?.geofences?.name ??
      link?.geofence?.name ??
      value?.reverse_geocoded_address?.formatted_address ??
      null,
  };
}

/**
 * Where the gateway is installed: the entity address item first, then the Device Setup
 * lat/lng, then the coordinates the gateway itself reports.
 */
export function resolveGatewaySite({ device, deviceSetupFields, paramValue }) {
  const item = device?.attributes?.EntityAddressItem;
  const address = item?.Address;
  const fromAddress = toPosition(address?.latitude, address?.longitude);
  const fromSetup = toPosition(deviceSetupFields?.latitude, deviceSetupFields?.longitude);
  const fromDevice = toPosition(paramValue?.parsed_ansi?.latitude, paramValue?.parsed_ansi?.longitude);
  const position = fromAddress ?? fromSetup ?? fromDevice;
  if (!position) return null;

  let source = "device";
  if (fromAddress) source = "address";
  else if (fromSetup) source = "setup";

  return {
    name: item?.name || address?.name || device?.attributes?.name || "Gateway site",
    address: [address?.street_address1, address?.city, address?.state, address?.postal_code]
      .filter(Boolean)
      .join(", "),
    position,
    source,
  };
}
